import { feedKey } from '../lib/feedContext';
/**
 * Бумажный режим: «взял бы» без денег и честный разбор через несколько минут.
 *
 * Новый человек приходит с двумя противоположными страхами — «а вдруг это не
 * работает» и «а вдруг я не успею», — и проверить их ему нечем, кроме своих
 * денег. Проверка деньгами дорогая и однократная: первая же неудачная вилка
 * (вторая нога ушла, аккаунт порезали, кэф оказался палпом) закрывает вопрос
 * навсегда и неправильно.
 *
 * Бумага снимает цену с этой проверки, не снимая с неё правдивости. Человек
 * делает ТЕ ЖЕ движения: открывает контору, копирует сумму, подтверждает первую
 * ногу, идёт за второй. Разница одна — денег в кассе нет. А замер остаётся:
 * сколько он потратил на самом деле, дожила ли вилка до этого момента, и что
 * получилось бы, если бы это были деньги.
 *
 * ## Почему разбор именно постфактум, а не сразу
 *
 * Сразу после нажатия ответа не существует. Вилка жива — и в этот момент она
 * жива у всех; вопрос в том, будет ли она жива через ваш темп. Поэтому судьба
 * бумажной ставки закрывается одним из двух событий: вилка умерла (тогда мы
 * знаем, сколько она прожила ПОСЛЕ отметки) или прошло больше вашего темпа
 * (тогда вы бы успели, и дальше её жизнь на ваш результат не влияет).
 *
 * ## Что здесь намеренно НЕ делается
 *
 * Не обещается исполнение. «Успели бы» означает «вилка была жива, когда вы бы
 * дошли до второй ноги», и ничего не говорит о том, приняла бы контора ставку,
 * не порезала бы лимит и не оказался бы кэф палпом. Обещать исполнение по нашей
 * ленте нельзя — мы видим витрину, а не кассу. Бумага честна ровно настолько,
 * насколько честна лента, и подписана именно так.
 *
 * Не считается «доходность за смену». Сумма бумажных прибылей — красивое число
 * без смысла: банк один, а вилки идут внахлёст, и сложить их значило бы
 * пообещать доход, который физически не поместился бы в деньги.
 *
 * На экране сейчас выключен (13.08, владелец). Логика, разбор и тесты остаются:
 * новичку проверка без кассы ещё пригодится. Как вернуть: `PAPER_UI` ниже в
 * `true` — подвал, карточка и отметка первой ноги заведутся сами.
 */

import { create } from 'zustand';

/** Показать бумажный режим в интерфейсе. Выключен, не удалён. */
export const PAPER_UI: boolean = false;

/** Ключ бумажного режима в хранилище вкладки. */
const KEY = feedKey('forq.paper');

/** Сколько бумажных отметок держим. Смена — это смена, а не вся история. */
export const KEEP = 200;

export interface Bet {
  id: string;
  /** Когда отметил, мс. */
  at: number;
  /** Что было обещано на момент отметки: процент и деньги по тогдашнему банку. */
  roiPct: number;
  profit: number;
  /** Темп человека на момент отметки, секунды. `null` — ещё не измерен. */
  tempo: number | null;
  /** Когда вилка ушла из ленты, мс. `null` — на момент разбора была ещё жива. */
  diedAt: number | null;
}

export type Outcome = 'open' | 'made' | 'missed' | 'unknown';

export interface PaperState {
  /** Включён ли режим. Новому человеку — включён: первая проверка не должна стоить денег. */
  on: boolean;
  bets: Bet[];
  setOn: (on: boolean) => void;
  mark: (bet: Bet) => void;
  bury: (id: string, at: number) => void;
  clear: () => void;
}

function saved(): boolean {
  try {
    const raw = localStorage.getItem(KEY);
    // Отсутствие записи — не «выключено», а «человек здесь впервые». Значения
    // по умолчанию у этих двух состояний разные, и путать их нельзя.
    return raw === null ? true : raw === '1';
  } catch {
    return true;
  }
}

export const usePaper = create<PaperState>((set, get) => ({
  on: saved(),
  bets: [],
  setOn: (on) => {
    try {
      localStorage.setItem(KEY, on ? '1' : '0');
    } catch {
      // Приватный режим — не повод не переключить.
    }
    set({ on });
  },
  mark: (bet) => {
    // Повторная отметка той же вилки — не вторая ставка, а второе нажатие.
    if (get().bets.some((one) => one.id === bet.id)) return;
    const kept = [...get().bets, bet];
    set({ bets: kept.length > KEEP ? kept.slice(kept.length - KEEP) : kept });
  },
  bury: (id, at) => {
    const bets = get().bets;
    if (!bets.some((one) => one.id === id && one.diedAt === null)) return;
    set({ bets: bets.map((one) => (one.id === id ? { ...one, diedAt: at } : one)) });
  },
  clear: () => set({ bets: [] }),
}));

/** Отметить бумажную ставку. Зовётся из карточки при подтверждении первой ноги. */
export function markPaper(
  id: string,
  roiPct: number,
  profit: number,
  tempo: number | null,
  now: number = Date.now(),
): void {
  usePaper.getState().mark({ id, at: now, roiPct, profit, tempo, diedAt: null });
}

/** Похоронить бумажную ставку: вилка ушла из ленты. Зовётся из хранилища экрана. */
export function buryPaper(id: string, at: number = Date.now()): void {
  usePaper.getState().bury(id, at);
}

/**
 * Судьба бумажной ставки.
 *
 * `unknown` — темп не измерен: сравнивать прожитое не с чем, и назвать это
 * успехом или промахом значило бы выбрать за человека.
 */
export function settle(bet: Bet, tempo: number | null, now: number): Outcome {
  const reference = bet.tempo ?? tempo;
  if (reference === null) return 'unknown';
  const alive = ((bet.diedAt ?? now) - bet.at) / 1000;
  if (alive >= reference) return 'made';
  // Жива и ещё не дотянула до темпа — исход не решён. Объявить его промахом
  // сейчас значило бы записать человеку неудачу, которой ещё не случилось.
  return bet.diedAt === null ? 'open' : 'missed';
}

export interface Ledger {
  marked: number;
  made: number;
  missed: number;
  open: number;
  /** Лучшая из тех, к которым не успели бы, в процентах. `null` — таких нет. */
  missedBest: number | null;
  /** Самая дорогая из состоявшихся, в рублях. `null` — таких нет. */
  bestRub: number | null;
}

/**
 * Итог по бумаге.
 *
 * Суммы прибыли здесь нет намеренно: банк один, вилки идут внахлёст, и сложить
 * их значило бы пообещать доход, который в деньги не поместился бы.
 */
export function ledger(bets: readonly Bet[], tempo: number | null, now: number): Ledger {
  let made = 0;
  let missed = 0;
  let open = 0;
  let missedBest: number | null = null;
  let bestRub: number | null = null;
  for (const bet of bets) {
    switch (settle(bet, tempo, now)) {
      case 'made':
        made += 1;
        if (bestRub === null || bet.profit > bestRub) bestRub = bet.profit;
        break;
      case 'missed':
        missed += 1;
        if (missedBest === null || bet.roiPct > missedBest) missedBest = bet.roiPct;
        break;
      case 'open':
        open += 1;
        break;
      default:
        break;
    }
  }
  return { marked: bets.length, made, missed, open, missedBest, bestRub };
}
